import React, { useState, useEffect, useRef } from 'react';
import { Calendar, MapPin, DollarSign, Tag, Send, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Chat } from '../types';
import { LoadingSpinner } from './LoadingSpinner';
import { USER_REPLY_TEMPLATES, ROUTES } from '../constants';
import { sendUserReply, sendBookNowAction } from '../services/api';
import { Badge } from './Badge';

interface ChatConversationViewProps {
  chat: Chat;
  userRole: 'user' | 'provider';
  onMessageSent: () => void;
}

export const ChatConversationView: React.FC<ChatConversationViewProps> = ({
  chat,
  userRole,
  onMessageSent,
}) => {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [bookingNow, setBookingNow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chat.messages]);

  const handleReplySelect = async (templateText: string) => {
    setSending(true);
    setError(null);

    try {
      const response = await sendUserReply({
        chatId: chat.chatId,
        text: templateText,
      });

      if (response.success) {
        onMessageSent();
      } else {
        setError(response.error || 'Failed to send reply. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleBookNow = async () => {
    setBookingNow(true);
    setError(null);

    try {
      const response = await sendBookNowAction({
        chatId: chat.chatId,
      });

      if (response.success) {
        onMessageSent();
      } else {
        setError(response.error || 'Failed to send Book Now action. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setBookingNow(false);
    }
  };

  const hasBookNowAction = chat.messages.some(
    (msg) => msg.type === 'action' && msg.action === 'BOOK_NOW'
  );

  const formatMessageTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleBookNowClick = () => {
    if (userRole === 'user' && chat.providerId && !chat.providerIsBlocked) {
      navigate(`${ROUTES.PROVIDER_DETAIL}/${chat.providerId}`);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-gray-200 bg-white p-3 sm:p-4 flex-shrink-0 max-h-64 overflow-y-auto">
        {userRole === 'user' && chat.providerIsBlocked && (
          <div className="mb-2 sm:mb-3 bg-yellow-50 border border-yellow-200 rounded-lg p-2 sm:p-3">
            <p className="text-xs sm:text-sm text-yellow-800 font-medium">
              This provider is no longer available. This conversation is now inactive.
            </p>
          </div>
        )}
        <div className="space-y-2 sm:space-y-3">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 truncate">{chat.postTitle}</h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1 truncate">
                {userRole === 'provider' ? `Chat with ${chat.userName || 'Service User'}` : `Chat with ${chat.providerName || 'Care Provider'}`}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 text-xs sm:text-sm">
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              <Tag className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-gray-500 text-xs">Care Type</p>
                <p className="text-gray-900 font-medium truncate">{chat.postCareType}</p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-gray-500 text-xs">Location</p>
                <p className="text-gray-900 font-medium truncate">{chat.postLocation}</p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-gray-500 text-xs">Date</p>
                <p className="text-gray-900 font-medium truncate">
                  {new Date(chat.postAppointmentDate).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              <DollarSign className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-gray-500 text-xs">Budget</p>
                <p className="text-gray-900 font-medium truncate">£{chat.postBudget}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-gray-50 p-3 sm:p-4 space-y-3 sm:space-y-4 min-h-0">
        {chat.messages.map((message) => {
          const isOwnMessage = message.sender === userRole;
          const isActionMessage = message.type === 'action';
          const isBookNowAction = isActionMessage && message.action === 'BOOK_NOW';
          const isClickableBookNow = isBookNowAction && userRole === 'user' && !chat.providerIsBlocked;

          return (
            <div
              key={message.messageId}
              className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
            >
              <div
                onClick={isClickableBookNow ? handleBookNowClick : undefined}
                className={`max-w-[85%] sm:max-w-md lg:max-w-xl px-3 sm:px-4 py-2 sm:py-3 rounded-lg ${
                  isActionMessage
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                    : isOwnMessage
                    ? 'bg-blue-600 text-white'
                    : userRole === 'provider'
                    ? 'bg-white text-gray-900 border border-gray-200'
                    : 'bg-green-100 text-gray-900'
                } ${
                  isClickableBookNow
                    ? 'cursor-pointer hover:from-green-600 hover:to-emerald-700 hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200'
                    : ''
                }`}
              >
                <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1 flex-wrap">
                  <span className="text-xs font-medium opacity-75">
                    {message.sender === 'provider' ? 'Provider' : 'Service User'}
                  </span>
                  {message.type === 'template' && (
                    <Badge variant="info">Template</Badge>
                  )}
                  {message.type === 'reply' && (
                    <Badge variant="success">Reply</Badge>
                  )}
                  {message.type === 'action' && (
                    <Badge variant="warning">Action</Badge>
                  )}
                </div>
                <div className="flex items-start gap-2">
                  {isActionMessage && <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 mt-0.5" />}
                  <p className={`text-xs sm:text-sm leading-relaxed whitespace-pre-wrap break-words ${isActionMessage ? 'font-semibold' : ''}`}>
                    {message.text}
                  </p>
                  {isClickableBookNow && (
                    <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 ml-1 animate-pulse flex-shrink-0 mt-0.5" />
                  )}
                </div>
                {isClickableBookNow && (
                  <p className="text-xs mt-1 opacity-90 font-medium">
                    Click to book with this provider
                  </p>
                )}
                <p
                  className={`text-xs mt-1 sm:mt-2 ${
                    isActionMessage || isOwnMessage ? 'text-blue-100' : 'text-gray-500'
                  }`}
                >
                  {formatMessageTime(message.createdAt)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {userRole === 'user' && (
        <div className="border-t border-gray-200 bg-white p-3 sm:p-4 flex-shrink-0 max-h-80 overflow-y-auto">
          {error && (
            <div className="mb-2 sm:mb-3 bg-red-50 border border-red-200 rounded-lg p-2 sm:p-3 text-xs sm:text-sm text-red-700">
              {error}
            </div>
          )}

          {chat.providerIsBlocked ? (
            <div className="text-center py-3 sm:py-4 text-gray-500">
              <p className="text-xs sm:text-sm">This conversation is inactive because the provider is no longer available.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-1.5 sm:gap-2">
                <Send className="h-3 w-3 sm:h-4 sm:w-4" />
                Send a reply:
              </h3>

              <div className="grid gap-1.5 sm:gap-2">
                {USER_REPLY_TEMPLATES.map((template, index) => (
                  <button
                    key={index}
                    onClick={() => handleReplySelect(template)}
                    disabled={sending}
                    className="text-left p-2 sm:p-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed group"
                  >
                    <p className="text-xs sm:text-sm text-gray-700 group-hover:text-gray-900">
                      {template}
                    </p>
                  </button>
                ))}
              </div>

              {sending && (
                <div className="flex items-center justify-center py-2">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-xs sm:text-sm text-gray-600">Sending reply...</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {userRole === 'provider' && (
        <div className="border-t border-gray-200 bg-white p-3 sm:p-4 flex-shrink-0">
          {error && (
            <div className="mb-2 sm:mb-3 bg-red-50 border border-red-200 rounded-lg p-2 sm:p-3 text-xs sm:text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-2 sm:space-y-3">
            <h3 className="text-xs sm:text-sm font-medium text-gray-700">Provider Actions</h3>

            <button
              onClick={handleBookNow}
              disabled={bookingNow || hasBookNowAction}
              className="w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-lg hover:from-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg text-xs sm:text-sm"
            >
              {bookingNow ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Sending Book Now...</span>
                </>
              ) : hasBookNowAction ? (
                <>
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Book Now Already Sent</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Send Book Now</span>
                </>
              )}
            </button>

            {hasBookNowAction && (
              <p className="text-xs text-gray-500 text-center">
                You have already sent a Book Now action for this conversation.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
